import React, { Fragment, useLayoutEffect, useRef, useState } from 'react';
import T from 'prop-types';

import Tooltip from '../../Tooltip';

const SEPARATOR = ', ';

const measureLayerStyle = {
  position: 'absolute',
  visibility: 'hidden',
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
  left: 0,
  top: 0,
};

const joinNodes = (nodes) =>
  nodes.map((node, i) => (
    <Fragment key={i}>
      {i > 0 && SEPARATOR}
      {node}
    </Fragment>
  ));

const TruncatedItems = ({ items }) => {
  const containerRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(items.length);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const compute = () => {
      const itemEls = container.querySelectorAll('[data-measure-item]');
      const sepEl = container.querySelector('[data-measure-sep]');
      const moreEl = container.querySelector('[data-measure-more]');
      if (!itemEls.length || !sepEl || !moreEl) return;

      const containerWidth = container.clientWidth;
      const itemWidths = Array.from(itemEls).map((el) => el.getBoundingClientRect().width);
      const sepWidth = sepEl.getBoundingClientRect().width;
      const moreWidth = moreEl.getBoundingClientRect().width;

      const totalWidth =
        itemWidths.reduce((sum, w) => sum + w, 0) + (itemWidths.length - 1) * sepWidth;

      let nextCount;
      if (totalWidth <= containerWidth) {
        nextCount = itemWidths.length;
      } else {
        // Reserve room for the "+N More" link, which sits after a separator.
        let acc = 0;
        let count = 0;
        for (let i = 0; i < itemWidths.length; i += 1) {
          acc += itemWidths[i];
          const candidateCount = i + 1;
          const width = acc + candidateCount * sepWidth + moreWidth;
          if (width <= containerWidth) count = candidateCount;
          else break;
        }
        nextCount = Math.max(count, 1);
      }

      setVisibleCount((prev) => (prev === nextCount ? prev : nextCount));
    };

    compute();
    const observer = new ResizeObserver(compute);
    observer.observe(container);
    return () => observer.disconnect();
  }, [items]);

  if (!items.length) return '-';

  const visible = items.slice(0, visibleCount);
  const overflowCount = items.length - visibleCount;

  return (
    <span ref={containerRef} className="truncated-items">
      {joinNodes(visible)}
      {overflowCount > 0 && (
        <>
          {SEPARATOR}
          <Tooltip
            content={
              <ul>
                {items.slice(visibleCount).map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            }
            colorVariant="light"
            overlayClassName="truncated-items-tooltip"
          >
            <span className="more-link">+{overflowCount} More</span>
          </Tooltip>
        </>
      )}

      {/* Hidden layer used only to measure natural widths. */}
      <span style={measureLayerStyle} aria-hidden>
        {items.map((item, i) => (
          <span data-measure-item key={i}>
            {item}
          </span>
        ))}
        <span data-measure-sep>{SEPARATOR}</span>
        <span data-measure-more className="more-link">
          +{items.length} More
        </span>
      </span>
    </span>
  );
};

TruncatedItems.propTypes = {
  items: T.array.isRequired,
};

export default TruncatedItems;
